import { ApiClient } from '@center-technologies/shared-api-clients';
import {
  Publisher,
  PublisherDetail,
  PublisherPermissions,
  PublisherSocials,
  PublisherMember,
  PublisherMemberRole,
  OrgDirectoryResult,
} from 'types/publisher';
import { MarketplaceActivity } from 'types/marketplace-activity';
import type { SagaProgress } from 'types/sagaProgress';
import {
  AudienceSummary,
  AuditTrailResponse,
  Channel,
  ChannelAccessRequest,
  ChannelCollaborator,
  ChannelInvite,
  ChannelItem,
  ChannelItemType,
  ChannelScope,
  ChannelStatus,
  ChannelSubscription,
  ChannelVisibility,
  CollaboratorRole,
  ContentFork,
  ContentPermission,
  ForkPolicy,
  ItemPermissionsResponse,
  ScopeMode,
  SphereChannelSubscription,
  SphereSubscriptionWithChannel,
  SubscriberSummary,
  SyndicateItemParams,
  EffectiveSubscription,
  IncomingChannelInvite,
} from 'types/channel';
import {
  MarketplaceMetrics,
  MarketplaceRating,
  MarketplaceReview,
  MarketplaceTargetType,
  MarketplaceDocument,
} from 'types/marketplace';
import {
  Approach,
  ApproachDefinition,
  ApproachDeploymentInput,
  ApproachVersion,
} from 'types/approach';
import { UnifiedFilters } from 'types/filters';
import { ArcTemplate } from 'types/arc';
import type { AspectRow } from 'types/aspect';
import type { GlyphColor } from '../types/glyph';
import type { LocalizedString } from 'types/context';

// ============================================================
// Request/Response Types
// ============================================================

export interface ListChannelsFilters {
  scope?: 'all' | ChannelScope;
  subscribed?: boolean;
  status?: ChannelStatus;
  contentStatus?: 'published' | 'draft' | 'all';
  useCaseTag?: string | string[];
  mine?: boolean;
  sortBy?: 'rating' | 'subscribers' | 'recent' | 'name';
  limit?: number;
  offset?: number;
  /** Opt-in per-row `can_manage_content` (see server route doc). One extra SpiceDB check per
   * row — only pass this when the caller actually needs to filter/gate on the capability. */
  includeCanManageContent?: boolean;
}

export interface ListChannelsResponse {
  channels: (Channel & {
    is_subscribed?: boolean;
    is_mandatory?: boolean;
    item_count?: number;
    subscriber_count?: number;
  })[];
  total: number;
}

export interface GetChannelResponse {
  channel: Channel & {
    item_count?: number;
    subscriber_count?: number;
  };
}

export interface GetChannelItemsOptions {
  itemType?: ChannelItemType;
  limit?: number;
  offset?: number;
}

export interface GetChannelItemsResponse {
  data: ChannelItem[];
  total: number;
}

export interface SubscribeResponse {
  subscription: ChannelSubscription;
}

export interface UnsubscribeResponse {
  status: string;
}

export interface CreateChannelData {
  name: { default: string; translations?: Record<string, string> };
  description?: { default: string; translations?: Record<string, string> };
  scope: ChannelScope;
  scopeId: string;
  visibility?: ChannelVisibility;
  useCaseTags?: string[];
  glyph?: string | null;
  publisherSystemName?: string;
}

export interface CreateChannelResponse {
  channel: Channel;
}

export interface UserCenter {
  id: string;
  name: string;
}

export interface GetUserCentersResponse {
  centers: UserCenter[];
}

/** Query-string parameters sent with a marketplace list request. */
type QueryParams = Record<string, string | number | boolean>;

// ============================================================
// API Functions (use schemaClient from ApiClientsContext)
// ============================================================

export async function listChannels(
  schemaClient: ApiClient,
  filters: ListChannelsFilters
): Promise<ListChannelsResponse> {
  const params: QueryParams = {};

  if (filters.scope) {
    params.scope = filters.scope;
  }
  if (filters.subscribed !== undefined) {
    params.subscribed = filters.subscribed;
  }
  if (filters.status) {
    params.status = filters.status;
  }
  if (filters.contentStatus) {
    params.contentStatus = filters.contentStatus;
  }
  if (filters.mine !== undefined) {
    params.mine = filters.mine;
  }
  if (filters.sortBy) {
    params.sortBy = filters.sortBy;
  }
  if (filters.limit !== undefined) {
    params.limit = filters.limit;
  }
  if (filters.offset !== undefined) {
    params.offset = filters.offset;
  }
  if (filters.includeCanManageContent !== undefined) {
    params.includeCanManageContent = filters.includeCanManageContent;
  }

  const useCaseTag = filters.useCaseTag;
  if (Array.isArray(useCaseTag) && useCaseTag.length > 0) {
    return schemaClient.get<ListChannelsResponse>('/channels/list', {
      params: { ...params, useCaseTag },
    });
  }
  if (typeof useCaseTag === 'string' && useCaseTag.length > 0) {
    params.useCaseTag = useCaseTag;
  }

  return schemaClient.get<ListChannelsResponse>('/channels/list', { params });
}

export async function getChannel(
  schemaClient: ApiClient,
  channelId: string
): Promise<GetChannelResponse> {
  return schemaClient.get<GetChannelResponse>(
    `/channels/${encodeURIComponent(channelId)}`
  );
}

// Org scope is derived from the bearer token server-side (EG9) — the route no
// longer accepts `organizationId`.
export async function getChannelItems(
  schemaClient: ApiClient,
  channelId: string,
  options?: GetChannelItemsOptions
): Promise<GetChannelItemsResponse> {
  const params: Record<string, string | number> = {};

  if (options?.itemType) {
    params.itemType = options.itemType;
  }
  if (options?.limit !== undefined) {
    params.limit = options.limit;
  }
  if (options?.offset !== undefined) {
    params.offset = options.offset;
  }

  return schemaClient.get<GetChannelItemsResponse>(
    `/channels/${encodeURIComponent(channelId)}/items`,
    { params }
  );
}

export interface ChannelItemLookupEntry {
  id: string;
  channel_id: string;
  channel_system_name: string;
  /**
   * `channels.name` is JSONB carrying a LocalizedString (`{default, translations}`) — NOT a
   * flat locale->string map. Typing it as `Record<string, string>` forced every consumer to
   * cast to the real shape before reading `default`, which is the type being wrong rather
   * than the callers. Some rows still carry a bare string, so both branches are declared.
   */
  channel_name?: LocalizedString | string;
  is_syndicated: boolean;
}

/**
 * Find the channel items publishing a given content system_name. Used by
 * consumer views to discover whether a manageable channel_item exists.
 */
export async function lookupChannelItems(
  schemaClient: ApiClient,
  itemType: ChannelItemType,
  itemSystemName: string
): Promise<ChannelItemLookupEntry[]> {
  const response = await schemaClient.get<{ items: ChannelItemLookupEntry[] }>(
    '/channels/items/lookup',
    { params: { itemType, itemSystemName } }
  );
  return response?.items ?? [];
}

export async function subscribe(
  schemaClient: ApiClient,
  channelId: string
): Promise<SubscribeResponse> {
  return schemaClient.post<SubscribeResponse>(
    `/channels/${encodeURIComponent(channelId)}/subscribe`,
    {}
  );
}

export async function unsubscribe(
  schemaClient: ApiClient,
  channelId: string
): Promise<UnsubscribeResponse> {
  return schemaClient.delete<UnsubscribeResponse>(
    `/channels/${encodeURIComponent(channelId)}/subscribe`
  );
}

// ── Personal ("You") identity subscriptions ──────────────────────────────────
// The subscriber is always the calling identity (self-subscribe) — no org/sphere
// fan-out. Grants only your identity `view` on the channel's templates.

export interface IdentityChannelSubscriptionSummary {
  channelSystemName: string;
  subscribedAt: string;
  channel: { id: string; name: unknown; description: unknown };
}

export async function subscribeIdentity(
  schemaClient: ApiClient,
  channelId: string
): Promise<{ subscription: unknown }> {
  return schemaClient.post(`/channels/identity-subscriptions`, { channelId });
}

export async function unsubscribeIdentity(
  schemaClient: ApiClient,
  channelId: string
): Promise<{ status: string }> {
  return schemaClient.delete(
    `/channels/identity-subscriptions/${encodeURIComponent(channelId)}`
  );
}

export async function listMyChannelSubscriptions(
  schemaClient: ApiClient
): Promise<{ subscriptions: IdentityChannelSubscriptionSummary[] }> {
  return schemaClient.get(`/channels/identity-subscriptions`);
}

export async function createChannel(
  schemaClient: ApiClient,
  data: CreateChannelData
): Promise<CreateChannelResponse> {
  return schemaClient.post<CreateChannelResponse>('/channels', data);
}

export interface UpdateChannelData {
  name?: { default: string; translations?: Record<string, string> };
  tagline?: { default: string; translations?: Record<string, string> } | null;
  description?: { default: string; translations?: Record<string, string> };
  tags?: string[];
  visibility?: ChannelVisibility;
  useCaseTags?: string[];
  isFeatured?: boolean;
  glyph?: string | null;
  glyph_kind?: 'letter' | 'icon';
  glyphKind?: 'letter' | 'icon';
  hue?: number;
  locales?: string[];
  defaultLocale?: string;
  scope?: ChannelScope;
  publisherSystemName?: string | null;
}

export interface UpdateChannelResponse {
  channel: Channel;
}

export async function updateChannel(
  schemaClient: ApiClient,
  channelId: string,
  data: UpdateChannelData
): Promise<UpdateChannelResponse> {
  return schemaClient.put<UpdateChannelResponse>(
    `/channels/${encodeURIComponent(channelId)}`,
    data
  );
}

// ============================================================
// Audience — Subscriber Summary & Export
// ============================================================

export async function getSubscriberSummary(
  schemaClient: ApiClient,
  channelId: string
): Promise<SubscriberSummary> {
  const response = await schemaClient.get<{ summary: SubscriberSummary }>(
    `/channels/${encodeURIComponent(channelId)}/subscribers/summary`
  );
  return response.summary;
}

export async function getChannelAudience(
  schemaClient: ApiClient,
  channelId: string,
  options?: { limit?: number; offset?: number }
): Promise<AudienceSummary> {
  const params: Record<string, number> = {};
  if (options?.limit !== undefined) params.limit = options.limit;
  if (options?.offset !== undefined) params.offset = options.offset;
  const response = await schemaClient.get<{ audience: AudienceSummary }>(
    `/channels/${encodeURIComponent(channelId)}/subscribers/audience`,
    { params }
  );
  return response.audience;
}

export async function exportSubscribers(
  schemaClient: ApiClient,
  channelId: string
): Promise<Blob> {
  return schemaClient.get<Blob>(
    `/channels/${encodeURIComponent(channelId)}/subscribers/export`,
    { responseType: 'blob' } as Record<string, unknown>
  );
}

// ============================================================
// Audience — Invites
// ============================================================

export async function listChannelInvites(
  schemaClient: ApiClient,
  channelId: string
): Promise<ChannelInvite[]> {
  const response = await schemaClient.get<{ invites: ChannelInvite[] }>(
    `/channels/${encodeURIComponent(channelId)}/invites`
  );
  return response.invites ?? [];
}

export async function createChannelInvite(
  schemaClient: ApiClient,
  channelId: string,
  email: string
): Promise<ChannelInvite> {
  const response = await schemaClient.post<{ invite: ChannelInvite }>(
    `/channels/${encodeURIComponent(channelId)}/invites`,
    { email }
  );
  return response.invite;
}

export async function revokeChannelInvite(
  schemaClient: ApiClient,
  channelId: string,
  inviteId: string
): Promise<void> {
  await schemaClient.delete(
    `/channels/${encodeURIComponent(channelId)}/invites/${encodeURIComponent(inviteId)}`
  );
}

export async function resendChannelInvite(
  schemaClient: ApiClient,
  channelId: string,
  inviteId: string
): Promise<ChannelInvite> {
  const response = await schemaClient.post<{ invite: ChannelInvite }>(
    `/channels/${encodeURIComponent(channelId)}/invites/${encodeURIComponent(inviteId)}/resend`,
    {}
  );
  return response.invite;
}

export async function getIncomingChannelInvites(
  schemaClient: ApiClient
): Promise<IncomingChannelInvite[]> {
  const response = await schemaClient.get<{ invites: IncomingChannelInvite[] }>(
    '/channels/invites/incoming'
  );
  return response.invites ?? [];
}

export async function acceptChannelInvite(
  schemaClient: ApiClient,
  channelId: string,
  inviteId: string
): Promise<ChannelInvite> {
  const response = await schemaClient.post<{ invite: ChannelInvite }>(
    `/channels/${encodeURIComponent(channelId)}/invites/${encodeURIComponent(inviteId)}/accept`,
    {}
  );
  return response.invite;
}

export async function declineChannelInvite(
  schemaClient: ApiClient,
  channelId: string,
  inviteId: string
): Promise<ChannelInvite> {
  const response = await schemaClient.post<{ invite: ChannelInvite }>(
    `/channels/${encodeURIComponent(channelId)}/invites/${encodeURIComponent(inviteId)}/decline`,
    {}
  );
  return response.invite;
}

// ============================================================
// Audience — Share Token
// ============================================================

export async function rotateShareToken(
  schemaClient: ApiClient,
  channelId: string
): Promise<{ token: string }> {
  return schemaClient.post<{ token: string }>(
    `/channels/${encodeURIComponent(channelId)}/share-token/rotate`,
    {}
  );
}

export async function publishChannel(
  schemaClient: ApiClient,
  channelId: string
): Promise<{ channel: Channel }> {
  return schemaClient.post<{ channel: Channel }>(
    `/channels/${encodeURIComponent(channelId)}/publish`,
    {}
  );
}

export async function unpublishChannel(
  schemaClient: ApiClient,
  channelId: string
): Promise<{ channel: Channel }> {
  return schemaClient.post<{ channel: Channel }>(
    `/channels/${encodeURIComponent(channelId)}/unpublish`,
    {}
  );
}

export async function approveChannel(
  schemaClient: ApiClient,
  channelId: string
): Promise<{ channel: Channel }> {
  return schemaClient.post<{ channel: Channel }>(
    `/channels/${encodeURIComponent(channelId)}/approve`,
    {}
  );
}

export async function rejectChannel(
  schemaClient: ApiClient,
  channelId: string,
  reason: string
): Promise<{ channel: Channel }> {
  return schemaClient.post<{ channel: Channel }>(
    `/channels/${encodeURIComponent(channelId)}/reject`,
    { reason }
  );
}

export async function getUserCenters(
  schemaClient: ApiClient
): Promise<GetUserCentersResponse> {
  return schemaClient.get<GetUserCentersResponse>(
    '/channels/user-centers'
  );
}

// ============================================================
// Grouped Channel Items Types & Functions
// ============================================================

export interface GetChannelItemsGroupedOptions {
  useCaseTag?: string[];
  limit?: number;
  offset?: number;
}

export interface ChannelItemUseCaseGroup {
  useCase: { system_name: string; display_name: string } | null;
  items: ChannelItem[];
}

export interface GetChannelItemsGroupedResponse {
  groups: ChannelItemUseCaseGroup[];
  total: number;
}

export async function getChannelItemsGrouped(
  schemaClient: ApiClient,
  channelId: string,
  options?: GetChannelItemsGroupedOptions
): Promise<GetChannelItemsGroupedResponse> {
  const params: QueryParams = {
    groupByUseCase: true,
  };

  if (options?.limit !== undefined) {
    params.limit = options.limit;
  }
  if (options?.offset !== undefined) {
    params.offset = options.offset;
  }

  return schemaClient.get<GetChannelItemsGroupedResponse>(
    `/channels/${encodeURIComponent(channelId)}/items`,
    {
      params: {
        ...params,
        ...(options?.useCaseTag?.length ? { useCaseTag: options.useCaseTag.join(',') } : {}),
      },
    }
  );
}

// ============================================================
// Marketplace Search Types & Functions (search-service)
// ============================================================

export interface MarketplaceSearchFilters {
  docType?: 'channel' | 'channel_item';
  useCaseTags?: string[];
  scope?: string;
  itemType?: string;
  minRating?: number;
}

export interface MarketplaceSearchParams {
  query: string;
  filters?: MarketplaceSearchFilters;
  sort?: 'relevance' | 'rating' | 'subscribers' | 'recent';
  limit?: number;
  offset?: number;
}

export interface FacetBucket {
  key: string;
  doc_count: number;
}

export interface MarketplaceSearchResponse {
  results: MarketplaceDocument[];
  total: number;
  facets: {
    use_case_tags: FacetBucket[];
    doc_type: FacetBucket[];
    item_type: FacetBucket[];
  };
}

export async function searchMarketplace(
  searchClient: ApiClient,
  params: MarketplaceSearchParams
): Promise<MarketplaceSearchResponse> {
  return searchClient.post<MarketplaceSearchResponse>(
    '/search/marketplace',
    params
  );
}

// ============================================================
// Marketplace Metrics Types & Functions
// ============================================================

export interface GetMetricsResponse {
  metrics: MarketplaceMetrics;
}

export interface BatchMetricsTarget {
  targetType: MarketplaceTargetType;
  targetId: string;
}

export interface BatchMetricsResponse {
  metrics: MarketplaceMetrics[];
}

export async function getMarketplaceMetrics(
  schemaClient: ApiClient,
  targetType: MarketplaceTargetType,
  targetId: string
): Promise<GetMetricsResponse> {
  return schemaClient.get<GetMetricsResponse>(
    `/marketplace/metrics/${encodeURIComponent(targetType)}/${encodeURIComponent(targetId)}`
  );
}

export async function batchGetMetrics(
  schemaClient: ApiClient,
  targets: BatchMetricsTarget[]
): Promise<BatchMetricsResponse> {
  return schemaClient.post<BatchMetricsResponse>(
    '/marketplace/metrics/batch',
    { targets }
  );
}

// ============================================================
// Marketplace Rating/Review Types & Functions
// ============================================================

export interface RatingWithReview extends MarketplaceRating {
  review?: MarketplaceReview;
  // Discover-safe author block resolved server-side (honors identity.discover).
  // Null when the rating's author is not discoverable by the caller.
  author?: ChannelReviewAuthor | null;
}

export interface GetRatingsResponse {
  ratings: RatingWithReview[];
  total: number;
  userRating: RatingWithReview | null;
}

export interface SubmitRatingData {
  targetType: MarketplaceTargetType;
  targetId: string;
  stars: number;
  review?: {
    title?: string | null;
    body: string;
  };
}

export interface SubmitRatingResponse {
  rating: RatingWithReview;
}

export interface DeleteRatingResponse {
  status: 'deleted';
}

export async function submitRating(
  schemaClient: ApiClient,
  data: SubmitRatingData
): Promise<SubmitRatingResponse> {
  return schemaClient.post<SubmitRatingResponse>(
    '/marketplace/ratings',
    data
  );
}

export async function getRatings(
  schemaClient: ApiClient,
  targetType: MarketplaceTargetType,
  targetId: string,
  options?: { limit?: number; offset?: number }
): Promise<GetRatingsResponse> {
  const params: Record<string, number> = {};

  if (options?.limit !== undefined) {
    params.limit = options.limit;
  }
  if (options?.offset !== undefined) {
    params.offset = options.offset;
  }

  return schemaClient.get<GetRatingsResponse>(
    `/marketplace/ratings/${encodeURIComponent(targetType)}/${encodeURIComponent(targetId)}`,
    { params }
  );
}

export async function deleteRating(
  schemaClient: ApiClient,
  ratingId: string
): Promise<DeleteRatingResponse> {
  return schemaClient.delete<DeleteRatingResponse>(
    `/marketplace/ratings/${encodeURIComponent(ratingId)}`
  );
}

// ============================================================
// Use Cases Types & Functions
// ============================================================

export interface UseCase {
  use_case_id: string;
  system_name: string;
  parent_system_name: string | null;
  name: string | null;
  description: string | null;
  icon: string | null;
  sort_order: number;
  localization_name_key: string;
  localization_description_key: string;
}

export async function listUseCases(schemaClient: ApiClient): Promise<UseCase[]> {
  return schemaClient.get<UseCase[]>('/use-cases');
}

// ============================================================
// Collaborator Types & Functions
// ============================================================

export interface GetCollaboratorsResponse {
  collaborators: ChannelCollaborator[];
  total: number;
}

export interface InviteCollaboratorData {
  identityId: string;
  role: CollaboratorRole;
}

export interface InviteCollaboratorResponse {
  collaborator: ChannelCollaborator;
}

export interface UpdateCollaboratorRoleData {
  role: CollaboratorRole;
}

export interface UpdateCollaboratorRoleResponse {
  collaborator: ChannelCollaborator;
}

export async function getChannelCollaborators(
  schemaClient: ApiClient,
  channelId: string
): Promise<GetCollaboratorsResponse> {
  return schemaClient.get<GetCollaboratorsResponse>(
    `/channels/${encodeURIComponent(channelId)}/collaborators`
  );
}

export async function inviteCollaborator(
  schemaClient: ApiClient,
  channelId: string,
  identityId: string,
  role: CollaboratorRole
): Promise<InviteCollaboratorResponse> {
  return schemaClient.post<InviteCollaboratorResponse>(
    `/channels/${encodeURIComponent(channelId)}/collaborators`,
    { identityId, role }
  );
}

export async function updateCollaboratorRole(
  schemaClient: ApiClient,
  channelId: string,
  identityId: string,
  role: CollaboratorRole
): Promise<UpdateCollaboratorRoleResponse> {
  return schemaClient.put<UpdateCollaboratorRoleResponse>(
    `/channels/${encodeURIComponent(channelId)}/collaborators/${encodeURIComponent(identityId)}`,
    { role }
  );
}

export async function removeCollaborator(
  schemaClient: ApiClient,
  channelId: string,
  identityId: string
): Promise<void> {
  return schemaClient.delete<void>(
    `/channels/${encodeURIComponent(channelId)}/collaborators/${encodeURIComponent(identityId)}`
  );
}

// ============================================================
// Publisher Member Types & Functions
// ============================================================

export interface GetPublisherMembersResponse {
  members: PublisherMember[];
  callerRole: PublisherMemberRole | null;
  callerIdentityId: string | null;
}

export interface AddPublisherMemberResponse {
  member: PublisherMember;
}

export interface UpdatePublisherMemberRoleResponse {
  member: PublisherMember;
}

export async function getPublisherMembers(
  schemaClient: ApiClient,
  publisherId: string
): Promise<GetPublisherMembersResponse> {
  return schemaClient.get<GetPublisherMembersResponse>(
    `/publishers/${encodeURIComponent(publisherId)}/members`
  );
}

export async function addPublisherMember(
  schemaClient: ApiClient,
  publisherId: string,
  identityId: string,
  role: 'manager' | 'creator'
): Promise<AddPublisherMemberResponse> {
  return schemaClient.post<AddPublisherMemberResponse>(
    `/publishers/${encodeURIComponent(publisherId)}/members`,
    { identityId, role }
  );
}

export async function updatePublisherMemberRole(
  schemaClient: ApiClient,
  publisherId: string,
  identityId: string,
  role: 'manager' | 'creator'
): Promise<UpdatePublisherMemberRoleResponse> {
  return schemaClient.put<UpdatePublisherMemberRoleResponse>(
    `/publishers/${encodeURIComponent(publisherId)}/members/${encodeURIComponent(identityId)}`,
    { role }
  );
}

export async function removePublisherMember(
  schemaClient: ApiClient,
  publisherId: string,
  identityId: string
): Promise<void> {
  return schemaClient.delete<void>(
    `/publishers/${encodeURIComponent(publisherId)}/members/${encodeURIComponent(identityId)}`
  );
}

export async function searchOrgDirectory(
  graphClient: ApiClient,
  search: string,
  limit = 20
): Promise<OrgDirectoryResult[]> {
  return graphClient.get<OrgDirectoryResult[]>('/identities/directory', {
    params: { search, limit },
  });
}

// ============================================================
// People Search (search-service — global, identity.discover gated)
// ============================================================

export interface PeopleSearchResult {
  identityId: string;
  subjectSphereId: string | null;
  displayName: string;
  email: string;
  glyph?: string | null;
  glyphColor?: GlyphColor | null;
  imageId?: string | null;
}

export interface PeopleSearchResponse {
  results: PeopleSearchResult[];
  total: number;
  returned: number;
}

/**
 * Global people search via search-service POST /search/people.
 * Results are permission-filtered server-side by identity.discover (fail-closed).
 * No organizationId param — caller org is read from the JWT.
 */
export async function searchPeople(
  searchClient: ApiClient,
  query: string,
  limit = 6,
  offset = 0
): Promise<PeopleSearchResponse> {
  return searchClient.post<PeopleSearchResponse>('/search/people', {
    query,
    limit,
    offset,
  });
}

// ============================================================
// Item Add Types & Functions
// ============================================================

export interface AddChannelItemData {
  itemType: ChannelItemType;
  itemId?: string;
  itemSystemName?: string;
  itemStatus?: 'draft' | 'published';
  itemVersion?: number;
  metadata?: {
    name?: { default: string; translations?: Record<string, string> };
    description?: { default: string; translations?: Record<string, string> };
    tags?: string[];
  };
}

export interface AddChannelItemResponse {
  item: ChannelItem;
}

export async function addChannelItem(
  schemaClient: ApiClient,
  channelId: string,
  data: AddChannelItemData
): Promise<AddChannelItemResponse> {
  return schemaClient.post<AddChannelItemResponse>(
    `/channels/${encodeURIComponent(channelId)}/items`,
    data
  );
}

// ============================================================
// Item Remove Types & Functions
// ============================================================

export async function removeChannelItem(
  schemaClient: ApiClient,
  channelId: string,
  itemId: string
): Promise<void> {
  await schemaClient.delete(
    `/channels/${encodeURIComponent(channelId)}/items/${encodeURIComponent(itemId)}`
  );
}

// ============================================================
// Item Reorder Types & Functions
// ============================================================

export interface ReorderItemsResponse {
  status: 'success';
}

export async function reorderItems(
  schemaClient: ApiClient,
  channelId: string,
  itemIds: string[]
): Promise<ReorderItemsResponse> {
  return schemaClient.patch<ReorderItemsResponse>(
    `/channels/${encodeURIComponent(channelId)}/items/reorder`,
    { item_ids: itemIds }
  );
}

// ============================================================
// Content Permissions Types & Functions
// ============================================================

export interface GetContentPermissionsResponse {
  permissions: ContentPermission | null;
}

export interface UpdateContentPermissionsData {
  allow_cloning: boolean;
  allow_sharing: boolean;
  attribution_required: boolean;
}

export interface UpdateContentPermissionsResponse {
  permissions: ContentPermission;
}

export async function getContentPermissions(
  schemaClient: ApiClient,
  contentType: string,
  contentId: string
): Promise<GetContentPermissionsResponse> {
  return schemaClient.get<GetContentPermissionsResponse>(
    `/content/${encodeURIComponent(contentType)}/${encodeURIComponent(contentId)}/permissions`
  );
}

export async function updateContentPermissions(
  schemaClient: ApiClient,
  contentType: string,
  contentId: string,
  permissions: UpdateContentPermissionsData
): Promise<UpdateContentPermissionsResponse> {
  return schemaClient.put<UpdateContentPermissionsResponse>(
    `/content/${encodeURIComponent(contentType)}/${encodeURIComponent(contentId)}/permissions`,
    permissions
  );
}

// ============================================================
// Fork & Attribution Types & Functions
// ============================================================

export interface ForkContentResponse {
  fork: ContentFork;
  forkedContentId: string;
  forkedContentType: string;
}

export interface GetForksResponse {
  forks: ContentFork[];
}

export interface AttributionEntry {
  content_type: string;
  content_id: string;
  owner_tenant_id?: string;
  forked_by?: string;
  forked_at?: string;
}

export interface GetAttributionResponse {
  attribution: {
    original: AttributionEntry;
    chain: AttributionEntry[];
  };
}

/**
 * Fork a channel item into the caller's organization. The target org is derived
 * server-side from the access token — it is a security boundary and is
 * deliberately not a client-supplied parameter.
 */
export async function forkContent(
  schemaClient: ApiClient,
  channelId: string,
  itemId: string
): Promise<ForkContentResponse> {
  return schemaClient.post<ForkContentResponse>(
    `/channels/${encodeURIComponent(channelId)}/items/${encodeURIComponent(itemId)}/fork`,
    {}
  );
}

export async function getContentForks(
  schemaClient: ApiClient,
  contentType: string,
  contentId: string
): Promise<GetForksResponse> {
  return schemaClient.get<GetForksResponse>(
    `/content/${encodeURIComponent(contentType)}/${encodeURIComponent(contentId)}/forks`
  );
}

export async function getAttribution(
  schemaClient: ApiClient,
  contentType: string,
  contentId: string
): Promise<GetAttributionResponse> {
  return schemaClient.get<GetAttributionResponse>(
    `/content/${encodeURIComponent(contentType)}/${encodeURIComponent(contentId)}/attribution`
  );
}

// ============================================================
// Content Search Types & Functions
// ============================================================

export interface ListApproachesResponse {
  approaches: {
    id: string;
    system_name: string;
    name?: { default?: string };
    description?: { default?: string };
    tags?: string[];
  }[];
  total: number;
}

export interface ListSurfaceTemplatesResponse {
  surface_templates: {
    id: string;
    system_name: string;
    name?: { default?: string };
    description?: { default?: string };
  }[];
  total: number;
}

export async function listApproaches(
  schemaClient: ApiClient,
  options?: { limit?: number }
): Promise<ListApproachesResponse> {
  const params: Record<string, string | number> = {};
  if (options?.limit !== undefined) {
    params.limit = options.limit;
  }
  return schemaClient.get<ListApproachesResponse>('/approaches/list', { params });
}

export async function listSurfaceTemplates(
  schemaClient: ApiClient,
  options?: { limit?: number }
): Promise<ListSurfaceTemplatesResponse> {
  const params: Record<string, string | number> = {};
  if (options?.limit !== undefined) {
    params.limit = options.limit;
  }
  return schemaClient.get<ListSurfaceTemplatesResponse>('/surface-templates/list', { params });
}

// ============================================================
// Publish-picker list fetchers for the five template supertypes
// (schema, context, arc_template, orbit_template, metaspace).
// Each mirrors listApproaches/listSurfaceTemplates: fetch the caller org's
// (+ system) rows, client-side substring filtering happens in useSearchContent.
// None of these routes accept a `limit` querystring param — unlike
// /approaches/list, so it is intentionally not sent.
// ============================================================

export interface ListSchemasResponse {
  schema_id: string;
  system_name: string;
  name?: string;
  description?: string;
}

export interface ListContextsResponse {
  id: string;
  system_name: string;
  name?: { default?: string } | string;
  description?: { default?: string } | string;
}

export interface ListArcTemplatesResponse {
  id: string;
  system_name: string;
  name?: string;
  category?: string;
}

export interface ListOrbitTemplatesResponse {
  id: string;
  system_name: string;
  name?: string;
}

// Org scope is derived from the token server-side; browser callers no longer send an
// `organizationId` param (the same removal applied to /contexts/list and /orbit-templates/).
export async function listSchemas(
  schemaClient: ApiClient
): Promise<ListSchemasResponse[]> {
  return schemaClient.get<ListSchemasResponse[]>('/schemas');
}

// Org scope is derived from the token server-side; `/contexts/list` no longer accepts an
// `organizationId` param (it let a caller aim the own-org branch at any tenant).
export async function listContexts(
  schemaClient: ApiClient
): Promise<ListContextsResponse[]> {
  return schemaClient.get<ListContextsResponse[]>('/contexts/list');
}

// Org scope is derived from the token server-side; browser callers no longer send an
// `organizationId` param.
export async function listArcTemplatesForSearch(
  schemaClient: ApiClient
): Promise<ListArcTemplatesResponse[]> {
  return schemaClient.get<ListArcTemplatesResponse[]>('/arc-templates/');
}

// Org scope is derived from the token server-side; `/orbit-templates/` no longer accepts an
// `organizationId` param (it went unchecked into the tenant filter and the Redis cache key).
export async function listOrbitTemplatesForSearch(
  schemaClient: ApiClient
): Promise<ListOrbitTemplatesResponse[]> {
  return schemaClient.get<ListOrbitTemplatesResponse[]>('/orbit-templates/');
}

export interface ListMetaspacesForSearchResponse {
  metaspaces: {
    metaspace_id: string;
    system_name: string;
    name: string;
    description: string;
  }[];
}

export async function listMetaspacesForSearch(
  schemaClient: ApiClient
): Promise<ListMetaspacesForSearchResponse> {
  return schemaClient.get<ListMetaspacesForSearchResponse>('/metaspaces', {
    params: { basic: true },
  });
}

// ============================================================
// Approach Detail Types & Functions (schema-service)
// ============================================================

export interface GetApproachOptions {
  version?: number;
  includeDraft?: boolean;
  includeMetrics?: boolean;
}

export interface GetApproachResponse {
  approach: Approach & {
    version?: ApproachVersion;
    latestVersion?: ApproachVersion;
    metrics?: MarketplaceMetrics;
  };
}

export interface GetApproachVersionsResponse {
  versions: ApproachVersion[];
}

export async function getApproach(
  schemaClient: ApiClient,
  systemName: string,
  options?: GetApproachOptions
): Promise<GetApproachResponse> {
  const params: QueryParams = {};

  if (options?.version !== undefined) {
    params.version = options.version;
  }
  if (options?.includeDraft !== undefined) {
    params.includeDraft = options.includeDraft;
  }
  if (options?.includeMetrics !== undefined) {
    params.includeMetrics = options.includeMetrics;
  }

  return schemaClient.get<GetApproachResponse>(
    `/approaches/${encodeURIComponent(systemName)}`,
    { params }
  );
}

export async function getApproachVersions(
  schemaClient: ApiClient,
  systemName: string
): Promise<GetApproachVersionsResponse> {
  return schemaClient.get<GetApproachVersionsResponse>(
    `/approaches/${encodeURIComponent(systemName)}/versions`
  );
}

/**
 * Sphere-context shape accepted by the flatten endpoint for evaluating
 * conditional sub-approach includes against a target sphere.
 *
 * Mirrors the backend `SphereContext` interface in
 * `schema-service/src/app/helpers/approaches/compositionHelpers.ts`.
 */
export interface FlattenSphereContext {
  properties?: Record<string, unknown>;
  perspectives?: Array<{
    systemName: string;
    assignees?: string[];
  }>;
  schemaSystemName?: string;
}

export interface FlattenApproachBody {
  version?: number;
  maxCascadeDepth?: number;
  sphereContext?: FlattenSphereContext;
}

/**
 * Response from `POST /approaches/:systemName/flatten`.
 *
 * TODO: promote `FlattenedApproachDefinition` to `types/approach.d.ts`
 * if/when the flattened definition diverges from `ApproachDefinition`.
 */
export interface FlattenApproachResponse {
  definition: ApproachDefinition;
}

/**
 * Flatten an approach definition by resolving all sub-approach `include`
 * references via deep merge.
 *
 * Backed by `POST /approaches/:systemName/flatten` in schema-service
 * (`flattenApproach.ts`). Used by the marketplace V2 "What's included?"
 * reader to render the fully-resolved tree.
 */
export async function flattenApproach(
  schemaClient: ApiClient,
  systemName: string,
  body: FlattenApproachBody
): Promise<FlattenApproachResponse> {
  return schemaClient.post<FlattenApproachResponse>(
    `/approaches/${encodeURIComponent(systemName)}/flatten`,
    body
  );
}

export async function getArcTemplate(
  schemaClient: ApiClient,
  systemName: string,
  opts?: {
    /**
     * Include unpublished versions. Pass ONLY when resolving a known-draft card (EAINT-873
     * owner decision 2026-09-01) — the flag also widens the route's subscribed-tenant rows,
     * so blanket use would surface other orgs' unpublished arcs.
     */
    includeDrafts?: boolean;
    /** Prefer the row with this version (a draft card's version) over the route's first row. */
    version?: number;
  }
): Promise<ArcTemplate | null> {
  const params: Record<string, string> = { systemName };
  if (opts?.includeDrafts) {
    params.includeDrafts = 'true';
  }

  const templates = await schemaClient.get<ArcTemplate[]>('/arc-templates/', { params });
  if (templates.length === 0) return null;
  if (opts?.version != null) {
    return templates.find((template) => template.version === opts.version) ?? templates[0];
  }
  return templates[0];
}

/**
 * Fetch an aspect by its system name (EAINT-873 Studio drawer). Backed by the
 * `assertTemplateViewable`-gated `GET /aspects/system_name/:systemName` — 403/404 (denied
 * or unknown) both resolve to `null` so the caller renders one collapsed not-found state
 * without learning which; any other failure (5xx, network) rethrows for the caller to
 * treat the same way at the UI layer.
 */
export async function getAspectBySystemName(
  schemaClient: ApiClient,
  systemName: string
): Promise<AspectRow | null> {
  try {
    return await schemaClient.get<AspectRow>(`/aspects/system_name/${encodeURIComponent(systemName)}`);
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 403 || status === 404) return null;
    throw error;
  }
}

/**
 * Lists the aspects a caller may attach to a type (EAINT-895 Phase 3d, composer's
 * "attach existing aspect" picker). Backed by `GET /aspects/list`, which 404s on an
 * empty result rather than returning `[]` — resolved here so the picker only ever
 * reasons about an array, never a 404 it would otherwise have to special-case.
 *
 * `targetOrganizationId` is the one deliberate organization argument: it reads ANOTHER tenant's
 * catalog (a definition's `tenant_id`). Omit it for the active organization.
 */
export async function listAspects(
  schemaClient: ApiClient,
  targetOrganizationId?: string
): Promise<AspectRow[]> {
  try {
    return await schemaClient.get<AspectRow[]>('/aspects/list', { params: { targetOrganizationId } });
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 404) return [];
    throw error;
  }
}

// ============================================================
// Sphere Channel Subscription Types & Functions
// ============================================================

export interface ListSphereSubscriptionsResponse {
  data: SphereSubscriptionWithChannel[];
  total: number;
}

export interface EffectiveSphereSubscriptionsResponse {
  data: EffectiveSubscription[];
}

export async function listSphereSubscriptions(
  schemaClient: ApiClient,
  sphereId: string,
  options?: { limit?: number; offset?: number }
): Promise<ListSphereSubscriptionsResponse> {
  const params: Record<string, string | number> = { sphereId };

  if (options?.limit !== undefined) {
    params.limit = options.limit;
  }
  if (options?.offset !== undefined) {
    params.offset = options.offset;
  }

  return schemaClient.get<ListSphereSubscriptionsResponse>(
    '/channels/sphere-subscriptions',
    { params }
  );
}

export async function getEffectiveSphereSubscriptions(
  schemaClient: ApiClient,
  sphereId: string
): Promise<EffectiveSphereSubscriptionsResponse> {
  return schemaClient.get<EffectiveSphereSubscriptionsResponse>(
    '/channels/sphere-subscriptions/effective',
    { params: { sphereId } }
  );
}

export async function subscribeSphereToChannel(
  schemaClient: ApiClient,
  channelId: string,
  sphereId: string,
  scopeMode: ScopeMode
): Promise<SphereSubscriptionWithChannel> {
  return schemaClient.post<SphereSubscriptionWithChannel>(
    '/channels/sphere-subscriptions',
    { channelId, sphereId, scopeMode }
  );
}

/** Record a recent-sphere touch in search-service Redis. Best-effort — callers should `.catch` silently. */
export async function recordRecentSphere(
  searchClient: ApiClient,
  sphereId: string
): Promise<void> {
  await searchClient.post<void>(`/recent-spheres/${encodeURIComponent(sphereId)}`);
}

export async function unsubscribeSphereFromChannel(
  schemaClient: ApiClient,
  subscriptionId: string,
  sphereId: string
): Promise<{ status: string }> {
  return schemaClient.delete<{ status: string }>(
    `/channels/sphere-subscriptions/${encodeURIComponent(subscriptionId)}`,
    { params: { sphereId } }
  );
}

export async function updateSphereSubscriptionScope(
  schemaClient: ApiClient,
  subscriptionId: string,
  scopeMode: ScopeMode,
  sphereId: string
): Promise<SphereSubscriptionWithChannel> {
  return schemaClient.patch<SphereSubscriptionWithChannel>(
    `/channels/sphere-subscriptions/${encodeURIComponent(subscriptionId)}`,
    { scopeMode },
    { params: { sphereId } }
  );
}

// ============================================================
// Subscribed Spheres (channel → spheres direction)
// ============================================================

/** A sphere subscription enriched with permission info from the backend. */
export interface SubscribedSphereItem extends SphereChannelSubscription {
  canManage: boolean;
}

export interface GetSubscribedSpheresResponse {
  data: SubscribedSphereItem[];
  total: number;
}

export interface GetSubscribedSpheresOptions {
  limit?: number;
  offset?: number;
}

export async function getSubscribedSpheres(
  schemaClient: ApiClient,
  channelId: string,
  options?: GetSubscribedSpheresOptions
): Promise<GetSubscribedSpheresResponse> {
  // organizationId is intentionally NOT sent — the backend derives it from the
  // caller's token (request.userProfile.organizationId). See
  // /standards/backend/service-architecture.md (org-from-token rule).
  const params: Record<string, string | number> = {};

  if (options?.limit !== undefined) {
    params.limit = options.limit;
  }
  if (options?.offset !== undefined) {
    params.offset = options.offset;
  }

  return schemaClient.get<GetSubscribedSpheresResponse>(
    `/channels/${encodeURIComponent(channelId)}/subscribed-spheres`,
    { params }
  );
}

// ============================================================
// Batch Sphere Name Enrichment (graph-service)
// ============================================================

/** Minimal sphere info returned by POST /spheres/batch-info. */
export interface BatchSphereInfo {
  id: string;
  label: string;
  name: string;
  type: string;
  avatar: string;
  imageId: string;
}

const BATCH_SPHERE_CHUNK_SIZE = 20;
const UNKNOWN_SPHERE_NAME = '(unknown sphere)';

/**
 * Fetches sphere names for an array of sphere IDs via graph-service batch endpoint.
 * Returns a Map of sphereId → display name. Deleted or inaccessible spheres
 * are mapped to a fallback name.
 */
export async function fetchSphereNames(
  graphClient: ApiClient,
  sphereIds: string[]
): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  if (sphereIds.length === 0) return result;

  const unique = [...new Set(sphereIds)];

  // Chunk into groups of BATCH_SPHERE_CHUNK_SIZE
  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += BATCH_SPHERE_CHUNK_SIZE) {
    chunks.push(unique.slice(i, i + BATCH_SPHERE_CHUNK_SIZE));
  }

  for (const chunk of chunks) {
    try {
      const response = await graphClient.post<{
        spheres: Record<string, BatchSphereInfo>;
      }>('/spheres/batch-info', { ids: chunk });

      if (response?.spheres) {
        for (const [id, info] of Object.entries(response.spheres)) {
          result.set(id, info.name || UNKNOWN_SPHERE_NAME);
        }
      }
    } catch {
      // Intentional: fall through to UNKNOWN_SPHERE_NAME fallback below.
      // Batch-info failures are non-fatal — callers display the fallback label.
    }
  }

  // Fill in fallback for any IDs that weren't returned
  for (const id of unique) {
    if (!result.has(id)) {
      result.set(id, UNKNOWN_SPHERE_NAME);
    }
  }

  return result;
}

// ============================================================
// Approach Deployment Types & Functions (graph-service)
// ============================================================

export interface PreviewApproachDeploymentResponse {
  steps: Record<string, unknown>[];
  conflicts: Record<string, unknown>[];
  deploymentInputs: ApproachDeploymentInput[];
  applicability: UnifiedFilters | null;
  applicabilityMatch: boolean;
}

export interface DeployApproachRequest {
  sphereId: string;
  approachSystemName: string;
  approachVersion?: number;
  deploymentInputValues?: Record<string, unknown>;
}

export interface DeployApproachResponse {
  deploymentId: string;
  status: string;
  steps: Record<string, unknown>[];
}

export async function previewApproachDeployment(
  graphClient: ApiClient,
  systemName: string,
  sphereId: string
): Promise<PreviewApproachDeploymentResponse> {
  return graphClient.get<PreviewApproachDeploymentResponse>(
    `/approaches/${encodeURIComponent(systemName)}/preview`,
    { params: { sphereId } }
  );
}

export async function deployApproach(
  graphClient: ApiClient,
  request: DeployApproachRequest
): Promise<DeployApproachResponse> {
  return graphClient.post<DeployApproachResponse>(
    '/approaches/deploy',
    request
  );
}

/**
 * Fetches one saga-progress snapshot for a deployment. Callers poll this via
 * `useSagaProgress` — this function itself makes a single request. A 403/404
 * from the underlying request propagates so the hook can classify it.
 */
export async function getDeploymentProgress(
  graphClient: ApiClient,
  deploymentId: string
): Promise<SagaProgress | null> {
  return graphClient.get<SagaProgress | null>(
    `/approaches/deployments/${encodeURIComponent(deploymentId)}/progress`
  );
}

// ============================================================
// Sphere Creation (graph-service)
// ============================================================

export interface CreateSphereRequest {
  schemaSystemName: string;
  /**
   * `true` captures the sphere in draft state — arc deployment and the canonical
   * SphereCreated event are deferred to `POST /spheres/:id/finalize`. Omitted is
   * the same as `false`; callers that depend on arcs deploying (the composer's
   * sandbox sphere) send `false` explicitly so the intent is legible.
   */
  draft?: boolean;
  sphere: {
    properties: Record<string, unknown>;
    assignedPerspectives: Array<{
      perspectiveProperties: { system_name: string };
      assigned: Array<{ sphere: { id: string } }>;
    }>;
  };
}

export interface CreateSphereResponse {
  status: string;
  sphere?: { id: string; [key: string]: unknown };
  sphereId?: string;
  ancestorIds: string[];
}

export async function createSphere(
  graphClient: ApiClient,
  request: CreateSphereRequest
): Promise<CreateSphereResponse> {
  return graphClient.post<CreateSphereResponse>('/spheres', request);
}

// ============================================================
// Eligible Spheres Types & Functions (graph-service)
// ============================================================

export interface EligibleSphere {
  id: string;
  name: string;
  type: string;
  typeName?: Record<string, string> | string;
  typeGlyph?: string;
  label: string;
  glyph: string;
  glyphColor?: GlyphColor;
  imageId?: string;
}

export interface GetEligibleSpheresParams {
  approachSystemName: string;
  search?: string;
  limit?: number;
  page?: number;
}

export interface GetEligibleSpheresResponse {
  spheres: EligibleSphere[];
  pagination: {
    page: number;
    limit: number;
    hasNextPage: boolean;
  };
  total: number;
}

/** Spheres an approach can be deployed onto (DeployApproachStepMolecule's target list); the MCP tool `get_eligible_spheres` reads the same route. */
export async function getEligibleSpheres(
  graphClient: ApiClient,
  params: GetEligibleSpheresParams
): Promise<GetEligibleSpheresResponse> {
  const queryParams: Record<string, string | number> = {};

  if (params.search) {
    queryParams.search = params.search;
  }
  if (params.limit !== undefined) {
    queryParams.limit = params.limit;
  }
  if (params.page !== undefined) {
    queryParams.page = params.page;
  }

  return graphClient.get<GetEligibleSpheresResponse>(
    `/approaches/${encodeURIComponent(params.approachSystemName)}/eligible-spheres`,
    { params: queryParams }
  );
}

// ============================================================
// Eligible Approaches Types & Functions (graph-service)
// ============================================================

export interface EligibleApproachItem {
  id: string;
  system_name: string;
  name: { default?: string; translations?: Record<string, string> } | string;
  description: { default?: string; translations?: Record<string, string> } | string | null;
  channel: { id: string; name: string; system_name?: string } | null;
  templateFor: string | null;
  childCount: number;
  factorCount: number;
  metaspaceCount: number;
  applicability: UnifiedFilters | null;
  official?: boolean;
  isNew?: boolean;
  suggestedBy?: string[];
}

export interface GetEligibleApproachesResponse {
  approaches: EligibleApproachItem[];
  pagination: {
    page: number;
    limit: number;
    hasNextPage: boolean;
  };
  total: number;
}

/**
 * Fetch approaches deployable to a sphere (applicability-matched), for the caller's org.
 * Org is read from the token server-side — no organizationId param.
 * Mirrors getEligibleSpheres but in the forward direction (sphere → approaches).
 */
export async function getEligibleApproaches(
  graphClient: ApiClient,
  sphereId: string,
  options?: { search?: string; limit?: number; page?: number }
): Promise<GetEligibleApproachesResponse> {
  const params: Record<string, string | number> = { sphereId };
  if (options?.search) params.search = options.search;
  if (options?.limit !== undefined) params.limit = options.limit;
  if (options?.page !== undefined) params.page = options.page;
  return graphClient.get<GetEligibleApproachesResponse>('/approaches/eligible', { params });
}

// ============================================================
// Channel Item Permissions (schema-service)
// ============================================================

/**
 * Fetch the full per-item permissions view: fork policy, explicit managers,
 * explicit fork grants, and the sharing/attribution flags from
 * content_permissions. Used by the Permissions tab of the item detail dialog.
 */
export async function getItemPermissions(
  schemaClient: ApiClient,
  channelId: string,
  itemId: string
): Promise<ItemPermissionsResponse> {
  return schemaClient.get<ItemPermissionsResponse>(
    `/channels/${encodeURIComponent(channelId)}/items/${encodeURIComponent(itemId)}/permissions`
  );
}

export async function updateForkPolicy(
  schemaClient: ApiClient,
  channelId: string,
  itemId: string,
  forkPolicy: ForkPolicy
): Promise<void> {
  await schemaClient.put<void>(
    `/channels/${encodeURIComponent(channelId)}/items/${encodeURIComponent(itemId)}/fork-policy`,
    { fork_policy: forkPolicy }
  );
}

export async function addItemManager(
  schemaClient: ApiClient,
  channelId: string,
  itemId: string,
  identityId: string
): Promise<void> {
  await schemaClient.post<void>(
    `/channels/${encodeURIComponent(channelId)}/items/${encodeURIComponent(itemId)}/managers`,
    { identity_id: identityId }
  );
}

export async function removeItemManager(
  schemaClient: ApiClient,
  channelId: string,
  itemId: string,
  identityId: string
): Promise<void> {
  await schemaClient.delete<void>(
    `/channels/${encodeURIComponent(channelId)}/items/${encodeURIComponent(itemId)}/managers/${encodeURIComponent(identityId)}`
  );
}

export async function addForkGrant(
  schemaClient: ApiClient,
  channelId: string,
  itemId: string,
  identityId: string
): Promise<void> {
  await schemaClient.post<void>(
    `/channels/${encodeURIComponent(channelId)}/items/${encodeURIComponent(itemId)}/fork-grants`,
    { identity_id: identityId }
  );
}

export async function removeForkGrant(
  schemaClient: ApiClient,
  channelId: string,
  itemId: string,
  identityId: string
): Promise<void> {
  await schemaClient.delete<void>(
    `/channels/${encodeURIComponent(channelId)}/items/${encodeURIComponent(itemId)}/fork-grants/${encodeURIComponent(identityId)}`
  );
}

// ============================================================
// Reference (subscribe) a channel item — template supertype
// consumption model
// ============================================================

export interface ReferenceChannelItemResponse {
  importedId: string;
  systemName: string;
  itemType: string;
}

/**
 * Reference (subscribe to) a channel item. Writes a `channel_subscriber`
 * SpiceDB tuple on the author's canonical row — never copies it. The
 * author's row remains the source of truth; see the spec's "consumption
 * model" (reference/subscribe, not copy).
 *
 * The referencing organization is derived server-side from the access token —
 * it is a security boundary and is deliberately not a client-supplied parameter.
 */
export async function referenceChannelItem(
  schemaClient: ApiClient,
  channelId: string,
  itemId: string
): Promise<ReferenceChannelItemResponse> {
  return schemaClient.post<ReferenceChannelItemResponse>(
    `/channels/${encodeURIComponent(channelId)}/items/${encodeURIComponent(itemId)}/import`,
    {}
  );
}

// ============================================================
// Channel Item Syndication (schema-service)
// ============================================================

/**
 * Response shape for `POST /channels/:channelId/items/syndicate`.
 * Backend returns both the new channel_items row and the
 * channel_item_syndications link row.
 */
export interface SyndicateItemResponse {
  channelItem: ChannelItem;
  syndication: Record<string, unknown>;
}

/**
 * Syndicate an item from another channel into the given target channel.
 * The backend derives `organizationId` from the caller's JWT and enforces
 * `manage_content` on the target channel + `allow_sharing` on the source
 * content.
 */
export async function syndicateItem(
  schemaClient: ApiClient,
  channelId: string,
  params: SyndicateItemParams
): Promise<SyndicateItemResponse> {
  const body: Record<string, unknown> = {
    source_channel_system_name: params.sourceChannelSystemName,
    source_item_type: params.sourceItemType,
    source_item_system_name: params.sourceItemSystemName,
    version_mode: params.versionMode,
  };

  if (params.pinnedVersion !== undefined) {
    body.pinned_version = params.pinnedVersion;
  }

  return schemaClient.post<SyndicateItemResponse>(
    `/channels/${encodeURIComponent(channelId)}/items/syndicate`,
    body
  );
}

/**
 * Remove a syndicated item reference from the given channel. Hard-deletes
 * the channel_items row and its SpiceDB relationships. Backend derives org
 * from JWT; callers need `manage_content` on the channel.
 */
export async function removeSyndication(
  schemaClient: ApiClient,
  channelId: string,
  itemId: string
): Promise<void> {
  await schemaClient.delete<void>(
    `/channels/${encodeURIComponent(channelId)}/items/${encodeURIComponent(itemId)}/syndication`
  );
}

// ============================================================
// Channel Item Audit Trail (schema-service)
// ============================================================

export interface GetItemAuditTrailOptions {
  limit?: number;
  cursor?: string;
}

/**
 * Fetch the paginated permission audit trail for a single channel item.
 * Used by the Audit tab of the item detail dialog.
 */
export async function getItemAuditTrail(
  schemaClient: ApiClient,
  channelId: string,
  itemId: string,
  options?: GetItemAuditTrailOptions
): Promise<AuditTrailResponse> {
  const params: Record<string, string | number> = {};

  if (options?.limit !== undefined) {
    params.limit = options.limit;
  }
  if (options?.cursor !== undefined) {
    params.cursor = options.cursor;
  }

  return schemaClient.get<AuditTrailResponse>(
    `/channels/${encodeURIComponent(channelId)}/items/${encodeURIComponent(itemId)}/audit-trail`,
    { params }
  );
}

// ============================================================
// Publisher API
// ============================================================

export interface ListPublishersFilters {
  mine?: boolean;
  status?: string;
  featured?: boolean;
  limit?: number;
  offset?: number;
}

export interface ListPublishersResponse {
  publishers: PublisherDetail[];
  total: number;
}

export interface GetPublisherResponse {
  publisher: PublisherDetail;
}

export interface FollowPublisherResponse {
  followerCount: number;
  isFollowed: boolean;
}

export interface GetPublisherPermissionsResponse {
  permissions: PublisherPermissions;
}

export async function listPublishers(
  schemaClient: ApiClient,
  filters: ListPublishersFilters
): Promise<ListPublishersResponse> {
  return schemaClient.get<ListPublishersResponse>('/publishers', {
    params: filters,
  });
}

export async function getPublisher(
  schemaClient: ApiClient,
  slug: string
): Promise<GetPublisherResponse> {
  return schemaClient.get<GetPublisherResponse>(
    `/publishers/${encodeURIComponent(slug)}`
  );
}

// ============================================================
// Composables Search (search-service — GET /composables/search)
// ============================================================

export type ComposablesSearchScope = { kind: 'org' } | { kind: 'approach'; systemName: string };

export interface ComposablesSearchParams {
  surface?: 'a2ui' | 'metaspace' | 'space';
  q?: string;
  /** Matches composition rows by stored kind, and code-backed catalog entries by `role` (EAINT-844 Phase F1). */
  kind?: 'surface' | 'section' | 'fragment' | 'primitive';
  scope?: ComposablesSearchScope;
}

export interface ComposablesSearchResult {
  systemName: string;
  name: string;
  kind: 'surface' | 'section' | 'fragment' | 'primitive' | null;
  origin: 'catalog' | 'template';
  tier: 'system' | 'organization';
  description: string | null;
}

export interface ComposablesSearchResponse {
  results: ComposablesSearchResult[];
  total: number;
  /** `true` when the definition-backed half of the merge failed (Elasticsearch outage) — the
   * composition results are missing, not merely empty. Callers should surface this distinctly
   * from "no matches". */
  degraded: boolean;
}

/**
 * Search the composition registry (merged code-backed catalog + published org compositions) via
 * `GET /composables/search`. `scope` is a client hint only — the server re-derives and enforces
 * approach-closure scoping from the token, never trusting this value (space-composer.md "Scope
 * model").
 */
export async function searchComposables(
  searchClient: ApiClient,
  params: ComposablesSearchParams = {}
): Promise<ComposablesSearchResponse> {
  const query: Record<string, string> = {};
  if (params.surface) query.surface = params.surface;
  if (params.q) query.q = params.q;
  if (params.kind) query.kind = params.kind;
  // `scope=approach:<systemName>` is the only recognized form server-side (invalid otherwise) —
  // org scope needs no param at all, since every result is already org-scoped from the token.
  if (params.scope?.kind === 'approach') {
    query.scope = `approach:${params.scope.systemName}`;
  }
  return searchClient.get<ComposablesSearchResponse>('/composables/search', { params: query });
}

export async function followPublisher(
  schemaClient: ApiClient,
  slug: string
): Promise<FollowPublisherResponse> {
  return schemaClient.post<FollowPublisherResponse>(
    `/publishers/${encodeURIComponent(slug)}/follow`,
    {}
  );
}

export async function unfollowPublisher(
  schemaClient: ApiClient,
  slug: string
): Promise<FollowPublisherResponse> {
  return schemaClient.delete<FollowPublisherResponse>(
    `/publishers/${encodeURIComponent(slug)}/follow`
  );
}

export async function getPublisherPermissions(
  schemaClient: ApiClient,
  slug: string
): Promise<GetPublisherPermissionsResponse> {
  return schemaClient.get<GetPublisherPermissionsResponse>(
    `/publishers/${encodeURIComponent(slug)}/permissions`
  );
}

export type PublisherChannel = Channel & {
  item_count?: number;
  subscriber_count?: number;
};

export async function getPublisherChannels(
  schemaClient: ApiClient,
  slug: string,
  opts?: { limit?: number; offset?: number }
): Promise<{ channels: PublisherChannel[]; total: number }> {
  return schemaClient.get<{ channels: PublisherChannel[]; total: number }>(
    `/publishers/${encodeURIComponent(slug)}/channels`,
    { params: opts }
  );
}

export interface UpdatePublisherData {
  displayName?: string;
  bio?: string;
  locales?: string[];
  defaultLocale?: string;
  website?: string | null;
  socials?: Partial<PublisherSocials> | null;
  hue?: number;
  glyph?: string;
  glyphKind?: 'letter' | 'icon';
}

export async function updatePublisher(
  schemaClient: ApiClient,
  slug: string,
  data: UpdatePublisherData
): Promise<{ publisher: Publisher }> {
  return schemaClient.patch<{ publisher: Publisher }>(
    `/publishers/${encodeURIComponent(slug)}`,
    data
  );
}

export async function submitPublisher(
  schemaClient: ApiClient,
  slug: string
): Promise<{ publisher: Publisher }> {
  return schemaClient.post<{ publisher: Publisher }>(
    `/publishers/${encodeURIComponent(slug)}/submit`,
    {}
  );
}

export async function approvePublisher(
  schemaClient: ApiClient,
  slug: string
): Promise<{ publisher: Publisher }> {
  return schemaClient.post<{ publisher: Publisher }>(
    `/publishers/${encodeURIComponent(slug)}/approve`,
    {}
  );
}

export async function rejectPublisher(
  schemaClient: ApiClient,
  slug: string,
  note: string
): Promise<{ publisher: Publisher }> {
  return schemaClient.post<{ publisher: Publisher }>(
    `/publishers/${encodeURIComponent(slug)}/reject`,
    { note }
  );
}

export async function archivePublisher(
  schemaClient: ApiClient,
  slug: string
): Promise<{ publisher: Publisher }> {
  return schemaClient.post<{ publisher: Publisher }>(
    `/publishers/${encodeURIComponent(slug)}/archive`,
    {}
  );
}

export interface GetPublisherActivityResponse {
  activity: MarketplaceActivity[];
  total: number;
}

export async function getPublisherActivity(
  schemaClient: ApiClient,
  slug: string,
  opts?: { limit?: number; offset?: number }
): Promise<GetPublisherActivityResponse> {
  return schemaClient.get<GetPublisherActivityResponse>(
    `/publishers/${encodeURIComponent(slug)}/activity`,
    { params: opts }
  );
}

// ============================================================
// Channel Reviews
// ============================================================

// Discover-safe author block. Null when the reviewer is not discoverable by the
// caller (the review stays visible; the author is obfuscated/anonymized).
export interface ChannelReviewAuthor {
  identityId: string;
  subjectSphereId: string | null;
  displayName: string;
  glyph: string | null;
  glyphColor: GlyphColor | null;
  imageId: string | null;
}

export interface ChannelReview {
  id: string;
  rating: number;
  text: string;
  createdAt?: string;
  author: ChannelReviewAuthor | null;
}

export interface GetChannelReviewsResponse {
  reviews: ChannelReview[];
  total: number;
}

// Caller org is read from the JWT server-side — never sent as a param (avoids a spoofable tenant field).
export async function getChannelReviews(
  schemaClient: ApiClient,
  channelId: string,
  opts?: { limit?: number; offset?: number }
): Promise<GetChannelReviewsResponse> {
  return schemaClient.get<GetChannelReviewsResponse>(
    `/channels/${encodeURIComponent(channelId)}/reviews`,
    { params: { ...opts } }
  );
}

// Caller org is read from the JWT server-side — never sent as a param.
export async function submitChannelForReview(
  schemaClient: ApiClient,
  channelId: string
): Promise<{ channel: Channel }> {
  return schemaClient.post<{ channel: Channel }>(
    `/channels/${encodeURIComponent(channelId)}/submit`,
    {}
  );
}

export interface GetChannelActivityResponse {
  activity: MarketplaceActivity[];
  total: number;
}

// Caller org is read from the JWT server-side — never sent as a param.
export async function getChannelActivity(
  schemaClient: ApiClient,
  channelId: string,
  opts?: { limit?: number; offset?: number }
): Promise<GetChannelActivityResponse> {
  return schemaClient.get<GetChannelActivityResponse>(
    `/channels/${encodeURIComponent(channelId)}/activity`,
    { params: { ...opts } }
  );
}

export interface CreatePublisherData {
  displayName: string;
  slug: string;
  bio?: string;
  hue?: number;
  glyph?: string;
  glyphKind?: 'letter' | 'icon';
}

export async function createPublisher(
  schemaClient: ApiClient,
  data: CreatePublisherData
): Promise<{ publisher: Publisher }> {
  return schemaClient.post<{ publisher: Publisher }>('/publishers', data);
}

export interface GetChannelByPublisherSlugResponse {
  channel: Channel;
  publisher: Publisher;
}

export async function getChannelByPublisherSlug(
  schemaClient: ApiClient,
  publisherSlug: string,
  channelSystemName: string
): Promise<GetChannelByPublisherSlugResponse> {
  return schemaClient.get<GetChannelByPublisherSlugResponse>(
    `/marketplace-channels/${encodeURIComponent(publisherSlug)}/${encodeURIComponent(channelSystemName)}`
  );
}

// ============================================================
// Marketplace Logo / Banner Upload & Delete (Phase 4)
// ============================================================

export interface MarketplaceAttachment {
  id: string;
  owner_type: 'publisher' | 'channel';
  owner_id: string;
  asset_kind: 'logo' | 'banner';
  blob_path: string;
  mime_type: string;
  size_bytes: number;
  scan_status: 'pending' | 'clean' | 'malicious' | 'quarantined';
  uploaded_at: string;
}

function buildMultipartForm(file: File): FormData {
  const form = new FormData();
  form.append('file', file);
  return form;
}

export async function uploadPublisherLogo(
  schemaClient: ApiClient,
  slug: string,
  file: File
): Promise<{ attachment: MarketplaceAttachment }> {
  return schemaClient.put<{ attachment: MarketplaceAttachment }>(
    `/publishers/${encodeURIComponent(slug)}/logo`,
    buildMultipartForm(file),
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
}

export async function deletePublisherLogo(
  schemaClient: ApiClient,
  slug: string
): Promise<void> {
  return schemaClient.delete<void>(`/publishers/${encodeURIComponent(slug)}/logo`);
}

export async function uploadPublisherBanner(
  schemaClient: ApiClient,
  slug: string,
  file: File
): Promise<{ attachment: MarketplaceAttachment }> {
  return schemaClient.put<{ attachment: MarketplaceAttachment }>(
    `/publishers/${encodeURIComponent(slug)}/banner`,
    buildMultipartForm(file),
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
}

export async function deletePublisherBanner(
  schemaClient: ApiClient,
  slug: string
): Promise<void> {
  return schemaClient.delete<void>(`/publishers/${encodeURIComponent(slug)}/banner`);
}

export async function uploadChannelLogo(
  schemaClient: ApiClient,
  channelId: string,
  file: File
): Promise<{ attachment: MarketplaceAttachment }> {
  return schemaClient.put<{ attachment: MarketplaceAttachment }>(
    `/channels/${encodeURIComponent(channelId)}/logo`,
    buildMultipartForm(file),
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
}

export async function deleteChannelLogo(
  schemaClient: ApiClient,
  channelId: string
): Promise<void> {
  return schemaClient.delete<void>(`/channels/${encodeURIComponent(channelId)}/logo`);
}

export async function deleteChannel(
  schemaClient: ApiClient,
  channelId: string
): Promise<{ status: string }> {
  return schemaClient.delete<{ status: string }>(
    `/channels/${encodeURIComponent(channelId)}`
  );
}

export async function archiveChannel(
  schemaClient: ApiClient,
  channelId: string
): Promise<{ channel: Channel }> {
  return schemaClient.post<{ channel: Channel }>(
    `/channels/${encodeURIComponent(channelId)}/archive`,
    {}
  );
}

export async function unarchiveChannel(
  schemaClient: ApiClient,
  channelId: string
): Promise<{ channel: Channel }> {
  return schemaClient.post<{ channel: Channel }>(
    `/channels/${encodeURIComponent(channelId)}/unarchive`,
    {}
  );
}

export interface TransferChannelData {
  newPublisherSystemName: string;
}

export async function transferChannel(
  schemaClient: ApiClient,
  channelSystemName: string,
  data: TransferChannelData
): Promise<void> {
  return schemaClient.post<void>(
    `/channels/${encodeURIComponent(channelSystemName)}/transfer`,
    data
  );
}

// ============================================================
// Audience — Access Requests (Phase 3)
// ============================================================

export async function createChannelAccessRequest(
  schemaClient: ApiClient,
  channelId: string,
  note?: string
): Promise<ChannelAccessRequest> {
  const response = await schemaClient.post<{ accessRequest: ChannelAccessRequest }>(
    `/channels/${encodeURIComponent(channelId)}/access-requests`,
    { note }
  );
  return response.accessRequest;
}

export async function listChannelAccessRequests(
  schemaClient: ApiClient,
  channelId: string
): Promise<ChannelAccessRequest[]> {
  const response = await schemaClient.get<{ accessRequests: ChannelAccessRequest[] }>(
    `/channels/${encodeURIComponent(channelId)}/access-requests`
  );
  return response.accessRequests ?? [];
}

export async function approveChannelAccessRequest(
  schemaClient: ApiClient,
  channelId: string,
  requestId: string
): Promise<ChannelAccessRequest> {
  const response = await schemaClient.post<{ accessRequest: ChannelAccessRequest }>(
    `/channels/${encodeURIComponent(channelId)}/access-requests/${encodeURIComponent(requestId)}/approve`,
    {}
  );
  return response.accessRequest;
}

export async function denyChannelAccessRequest(
  schemaClient: ApiClient,
  channelId: string,
  requestId: string
): Promise<ChannelAccessRequest> {
  const response = await schemaClient.post<{ accessRequest: ChannelAccessRequest }>(
    `/channels/${encodeURIComponent(channelId)}/access-requests/${encodeURIComponent(requestId)}/deny`,
    {}
  );
  return response.accessRequest;
}

// ─── Applicable-templates picker (EAINT-687 / EAINT-689) ─────────────────────

export type TemplateSource = 'identity_subscription' | 'sphere_subscription' | 'system';

export interface TemplateChannel {
  systemName: string;
  displayName: string;
  subscribed: boolean;
  marketplace: boolean;
}

export interface TemplateCounts {
  subApproaches: number;
  providedTypes: number;
  providedContexts: number;
}

export interface Template {
  systemName: string;
  displayName: string;
  description?: string;
  version: number;
  /** The sphere type this template creates (definition.templateFor). */
  templateFor: string;
  channel: TemplateChannel | null;
  counts: TemplateCounts;
  official: boolean;
  isNew?: boolean;
  recommended: boolean;
  /** How this template became reachable for the caller. */
  source: TemplateSource;
  deploymentInputs: unknown[];
}

export interface GetTemplatesResponse {
  templates: Template[];
  total: number;
}

export interface GetApplicableTemplatesParams {
  /** One or more sphere types (definition.templateFor). */
  templateFor: string | string[];
  /**
   * Optional parent sphere the caller is adding within. Requires `edit` on it
   * (enforced server-side); folds the sphere's channel subscriptions into the
   * reachable set. Omit for org creation (no parent).
   */
  sphereId?: string;
}

/**
 * Returns the creation-templates for one or more sphere types, sourced from the
 * caller's channel subscriptions, an optional parent sphere's subscriptions, and
 * system templates. Single round-trip. Powers both the add-org picker
 * (`templateFor: 'center_organization'`) and the add-sphere dialog (the selected
 * type + the parent `sphereId`). Replaces the old org-only `getOrgTemplates`.
 */
export async function getApplicableTemplates(
  schemaClient: ApiClient,
  params: GetApplicableTemplatesParams
): Promise<GetTemplatesResponse> {
  const templateFor = Array.isArray(params.templateFor)
    ? params.templateFor.join(',')
    : params.templateFor;
  const qs = new URLSearchParams({ templateFor });
  if (params.sphereId) qs.set('sphereId', params.sphereId);
  return schemaClient.get<GetTemplatesResponse>(`/approaches/templates?${qs.toString()}`);
}
